import type { DataTableColumn } from '@odo/shared-ui';
import type { VirtualAppointmentOverviewItem } from '../../../services/virtualAppointmentsOverview';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { VirtualAppointmentStatusChip } from '../../virtual-appointments/VirtualAppointmentStatusChip';
import { TimeCell } from '../../shared/TimeCell';
import {
    OverviewPatientCell,
    OverviewTriggerCell,
    OverviewTypeCell
} from './VirtualAppointmentOverviewCells';
import type { OverviewSortKey } from './overviewSort';
import type { OverviewTabConfig } from './overviewTabs';

type OverviewColumn = DataTableColumn<VirtualAppointmentOverviewItem, OverviewSortKey>;

export interface OverviewColumnOptions {
    tab: OverviewTabConfig;
    /** The detail pane is open: keep only what identifies a row, so nothing gets squeezed. */
    compact: boolean;
    todayIso: string;
    doctorByPracticeId: ReadonlyMap<string, string>;
}

const CELL = 'px-3 py-0 overflow-hidden';
const HEADER = 'px-3';

// Relative weights, normalised over the visible columns so any subset still fills the table.
const WEIGHTS: Readonly<Record<string, number>> = {
    patient: 25,
    va: 25,
    team: 11,
    trigger: 16,
    status: 14,
    created: 11,
    closed: 11
};

// Overwritten by withWidths once the visible set is known.
const AUTO_WIDTH = '';

function withWidths(columns: OverviewColumn[]): OverviewColumn[] {
    const total = columns.reduce((sum, column) => sum + (WEIGHTS[column.key] ?? 0), 0);
    return columns.map((column) => ({
        ...column,
        width: `${(((WEIGHTS[column.key] ?? 0) / total) * 100).toFixed(2)}%`
    }));
}

export function buildOverviewColumns(opts: OverviewColumnOptions): OverviewColumn[] {
    const { tab, compact, todayIso, doctorByPracticeId } = opts;
    const columns: OverviewColumn[] = [
        {
            key: 'patient',
            width: AUTO_WIDTH,
            label: 'PATIENT',
            cellClassName: 'pl-4 pr-3 py-0 overflow-hidden',
            headerClassName: 'pl-4 pr-3',
            render: (item) => (
                <OverviewPatientCell
                    item={item}
                    doctorName={
                        item.patient.practiceId
                            ? (doctorByPracticeId.get(item.patient.practiceId) ?? null)
                            : null
                    }
                />
            )
        },
        {
            key: 'va',
            width: AUTO_WIDTH,
            label: 'VA',
            cellClassName: CELL,
            headerClassName: HEADER,
            render: (item) => <OverviewTypeCell item={item} />
        },
        {
            key: 'team',
            width: AUTO_WIDTH,
            label: 'TEAM',
            visible: !compact,
            cellClassName: CELL,
            headerClassName: HEADER,
            render: (item) => <TeamPill team={item.team} size="xs" />
        },
        {
            key: 'status',
            width: AUTO_WIDTH,
            label: 'STATUS',
            cellClassName: CELL,
            headerClassName: HEADER,
            render: (item) => <VirtualAppointmentStatusChip status={item.status} size="xs" />
        },
        {
            key: 'trigger',
            width: AUTO_WIDTH,
            label: 'TRIGGER',
            cellClassName: CELL,
            headerClassName: HEADER,
            render: (item) => <OverviewTriggerCell item={item} todayIso={todayIso} />
        },
        {
            key: 'closed',
            width: AUTO_WIDTH,
            label: 'CLOSED',
            visible: tab.showClosed && !compact,
            cellClassName: CELL,
            headerClassName: HEADER,
            // Canceled VAs carry no resolvedAt, so their last update stands in as the close date.
            render: (item) => <TimeCell iso={item.resolvedAt ?? item.updatedAt} tier={null} />
        },
        {
            key: 'created',
            width: AUTO_WIDTH,
            label: 'CREATED',
            sortable: true,
            sortKey: 'createdAt',
            visible: !compact,
            cellClassName: CELL,
            headerClassName: HEADER,
            render: (item) => <TimeCell iso={item.createdAt} tier={null} />
        }
    ];
    return withWidths(columns.filter((column) => column.visible !== false));
}
